import { useCallback, useMemo } from "react";
import { generatePath } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { PATH_CONTRACTOR } from "@/constants";
import { openNewWindow } from "@/lib/utils/openNewWindow";
import {
  fetchContestAgreementDetail,
  ContestAgreementsService,
} from "@/services/contestAgreements";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { useToast } from "@/components/ui/use-toast";
import type { AttachmentGroup } from "@/components/AttachmentsDisplaySection";
import { useContestAgreementDetailBreadcrumbs } from "./useContestAgreementDetailBreadcrumbs";
import { mapContestAgreementFilesToAttachmentGroups } from "../utils/mapContestAgreementFilesToAttachmentGroups";
export function useContestAgreementDetail(id: string) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const sendLinkMutation = useMutationWithErrorHandling({
    mutationFn: ContestAgreementsService.sendLink,
    errorTitle: t("contestAgreements.errors.resendLink"),
    onSuccess: () => {
      toast({
        title: t("contestAgreements.detail.resendLinkSuccess"),
      });
      queryClient.invalidateQueries({
        queryKey: ["contestAgreementDetail", id],
      });
    },
  });

  const query = useQuery({
    queryKey: ["contestAgreementDetail", id],
    queryFn: () => {
      return fetchContestAgreementDetail(id);
    },
  });

  const breadcrumbItems = useContestAgreementDetailBreadcrumbs({
    label: query.data?.contractor_full_name ?? query.data?.id ?? id ?? "",
  });

  const attachmentGroups = useMemo((): AttachmentGroup[] => {
    if (!query.data?.files?.length) return [];
    return mapContestAgreementFilesToAttachmentGroups(
      query.data.files,
      (fileType) => t(`contestAgreements.detail.fileType.${fileType}`),
    );
  }, [query.data?.files, t]);

  const handleGoToContractor = useCallback(() => {
    const url = generatePath(PATH_CONTRACTOR, {
      contractorId: query.data?.contractor_id ?? "",
    });
    openNewWindow(url);
  }, [query.data?.contractor_id]);

  return {
    query,
    breadcrumbItems,
    attachmentGroups,
    sendLinkMutation,
    handleGoToContractor,
  };
}
